<template>
  <div class="container-fluid">
    <certificate-datatable-header :total-rows="additionalInfo.totalRows"></certificate-datatable-header>
    <b-table
      id="table-search"
      responsive="sm"
      @context-changed="onContextChanged"
      :items="itemsProvider"
      :fields="columnsVisible"
      table-class="mb-0"
      class="mb-5"
      :current-page="tableContext.currentPage"
      :per-page="tableContext.perPage"
      :sort-by="tableContext.sortBy"
      :sort-desc="tableContext.sortDesc"
      :filter="tableContext.filter"
      no-local-sorting
      no-sort-reset
      show-empty
    >
      <template v-slot:head(selected)>
        <b-form-checkbox :checked="isToggleAllChecked" @change="onToggleAllChanged"></b-form-checkbox>
      </template>
      <template v-slot:cell(selected)="row">
        <b-form-checkbox v-model="selectedItems" :value="row.item.id"></b-form-checkbox>
      </template>
      <template v-slot:cell(subjectDn)="row">
        <b-link :to="{ name: 'certificate-detail', params: { id: row.item.id } }">{{ row.value }}</b-link>
      </template>
      <template v-slot:cell(serialNumber)="row">
        <b-link :to="{ name: 'certificate-detail', params: { id: row.item.id } }">{{ row.value }}</b-link>
      </template>
      <template v-slot:cell(actions)="row">
        <b-dropdown id="action-drop-down" variant="link" toggle-class="text-decoration-none" no-caret right>
          <template v-slot:button-content>
            <a class="table-actions-button dropdown-toggle dropdown-toggle--no-caret" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false" :id="`table-actions-button`">
              <span class="sr-only">Open actions</span>
              <mtg-icon icon="ic-dots" viewBox="0 0 6 22"></mtg-icon>
            </a>
          </template>

          <template v-slot:head(actions)>
            <span class="text-info"></span>
          </template>
          <b-dropdown-item :to="{ name: 'certificate-detail', params: { id: row.item.id } }">{{ $t("lang.label_certificate_details") }}</b-dropdown-item>
          <b-dropdown-item v-if="row.item.certStatus !== 'REVOKED'" @click="revokeCertificate(row)">{{$t("lang.label_certificate_revoke_button") }}</b-dropdown-item>
          <b-dropdown-item @click="downloadCertificate(row)">{{ $t("lang.label_certificate_download") }}</b-dropdown-item>
          <b-dropdown-item @click="createSimilarCertificate(row)">{{ $t("lang.label_certificate_createSimilar") }}</b-dropdown-item>
        </b-dropdown>
      </template>
    </b-table>
    <b-row v-if="additionalInfo.totalRows">
      <b-col>
        <b-dropdown id="items-per-page-button" dropup variant="outline-primary" v-model="tableContext.perPage" toggle-class="text-decoration-none">
          <template v-slot:button-content>
            {{ tableContext.perPage }}
          </template>
          <b-dropdown-item @click="onPerPageChanged(50)">50</b-dropdown-item>
          <b-dropdown-item @click="onPerPageChanged(40)">40</b-dropdown-item>
          <b-dropdown-item @click="onPerPageChanged(30)">30</b-dropdown-item>
          <b-dropdown-item @click="onPerPageChanged(20)">20</b-dropdown-item>
          <b-dropdown-item @click="onPerPageChanged(10)">10</b-dropdown-item>
        </b-dropdown>

        <p class="mb-0 d-inline-block mt-2 ml-3" v-if="!isLoading">
          {{ $t("lang.label_certificate_showing", [additionalInfo.startIndex, additionalInfo.endIndex, additionalInfo.totalRows]) }}
        </p>
      </b-col>
      <b-col>
        <div class="mb-5">
          <b-pagination v-model="tableContext.currentPage" :total-rows="additionalInfo.totalRows" :per-page="tableContext.perPage" align="right"></b-pagination>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import moment from "moment";
import CertificateDatatableHeader from "./CertificateDatatableHeader";
import * as utils from "@/utils";

export default {
  name: "CertificateDatatable",
  props: {
    itemsProvider: {
      type: Function,
      required: true
    },
    items: {
      type: Array,
      required: true
    },
    tableContext: {
      type: Object,
      required: true
    },
    additionalInfo: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      /* Columns */
      columns: [
        { key: "selected", label: "", sortable: false, order: 0 },
        {
          key: "subjectDn",
          sortable: true,
          label: this.$t("lang.label_certificate_subjectDn"),
          tdClass: "break-text-all"
        },
        {
          key: "issuerDn",
          sortable: true,
          label: this.$t("lang.label_certificate_issuerDn"),
          tdClass: "break-text-all"
        },
        {
          key: "serialNumber",
          sortable: true,
          label: this.$t("lang.label_certificate_serialNumber"),
          tdClass: "break-text-all"
        },
        {
          key: "certPublicKeyAlgorithm",
          sortable: true,
          label: this.$t("lang.label_certificate_certPublicKeyAlgorithm"),
          formatter: value => {
            return this.$t("lang.enum_publicKeyAlgorithm_" + value);
          }
        },
        {
          key: "certSignatureAlgorithm",
          sortable: true,
          label: this.$t("lang.label_certificate_certSignatureAlgorithm"),
          formatter: value => {
            return this.$t("lang.enum_signatureAlgorithm_" + value);
          }
        },
        {
          key: "certUsageType",
          sortable: true,
          label: this.$t("lang.label_certificate_certUsageType"),
          formatter: value => {
            return this.$t("lang.enum_keyUsageType_" + value);
          }
        },
        {
          key: "validNotBefore",
          sortable: true,
          label: this.$t("lang.label_certificate_validNotBefore"),
          formatter: value => {
            return moment(String(value)).format(utils.constants.DATE_PATTERN);
          }
        },
        {
          key: "validNotAfter",
          sortable: true,
          label: this.$t("lang.label_certificate_validNotAfter"),
          formatter: value => {
            return moment(String(value)).format(utils.constants.DATE_PATTERN);
          }
        },
        {
          key: "certStatus",
          sortable: true,
          label: this.$t("lang.label_certificate_certStatus"),
          tdClass: this.getVariant,
          formatter: value => {
            return this.$t("lang.enum_certificateStatus_" + value);
          }
        },
        {
          key: "actions",
          sortable: false,
          label: "",
          thClass: ""
        }
      ]
    };
  },
  computed: {
    isLoading() {
      return this.$store.getters["isLoading"];
    },
    columnsVisible() {
      const visibleFromStore = this.$store.getters["certificate/columnsVisible"];
      const visibleAll = ["selected", "actions"].concat(visibleFromStore);
      return this.columns.filter(value => visibleAll.includes(value.key));
    },
    /* Selection */
    selectedItems: {
      get: function() {
        return this.$store.getters["certificate/selectedCertificates"];
      },
      set: function(newValue) {
        this.$store.dispatch("certificate/setSelectedCertificates", newValue);
      }
    },
    isToggleAllChecked() {
      if (this.selectedItems.length === 0) {
        return false;
      }

      // Sort and compare the arrays
      const currentItemsIds = this.items.map(item => item.id).sort();
      const selectedItemsIds = this.selectedItems.slice(0).sort();
      return JSON.stringify(currentItemsIds) === JSON.stringify(selectedItemsIds);
    },
    statistics() {
      return this.$store.getters["certificate/certificateStatistics"]; // we need to get statistics for Issued Certificates
    }
  },
  methods: {
    onContextChanged(ctx) {
      this.$emit("change", ctx);
    },
    onPerPageChanged(perPage) {
      this.tableContext.perPage = perPage;
    },
    onToggleAllChanged(checked) {
      const currentPageIds = this.items.map(item => item.id);
      if (checked) {
        this.$store.dispatch("certificate/addSelectedCertificates", currentPageIds); // We're adding
      } else {
        this.$store.dispatch("certificate/removeSelectedCertificates", currentPageIds); // We're removing
      }
    },
    revokeCertificate(data) {
      this.$bvModal
        .msgBoxConfirm(this.$t("lang.message_certificate_revoke_confirmation_message"))
        .then(value => {
          if (value) {
            this.$store
              .dispatch("certificate/revokeCertificate", { certId: data.item.id })
              .then(() => {
                this.$store.dispatch("alert/showSuccessAlert", this.$t("lang.message_certificate_revoke_success_message"));
                // Reload
                this.$router.go();
              })
              .catch(e => {
                if (e.response.data.message === "Certificate is already revoked or expired.") {
                  this.$store.dispatch("alert/showFailureAlert", this.$t("lang.message_certificate_already_revoked_expired_message"));
                } else {
                  this.$store.dispatch("alert/showFailureAlert", this.$t("lang.message_certificate_revoke_error_message"));
                }
              });
          }
        })
        .catch(() => {});
    },
    downloadCertificate(data) {
      this.$store
        .dispatch("certificate/downloadCertificate", { certId: data.item.id })
        .then(response => {
          utils.downloadCertFile(data.item.subjectCommonName + ".pem", response.data.pemCertificate);
        })
        .catch(() => {
          this.$store.dispatch("alert/showFailureAlert", this.$t("lang.message_certificate_download_error_message"));
        });
    },
    createSimilarCertificate(data) {
      const issuedCerts = this.statistics.data.certsIssued;
      if (issuedCerts.issued === issuedCerts.max) {
        this.$bvModal.show("modal-noIssuedCerts");
      } else {
        this.$router.push({ name: "certificate-copy", params: { id: data.item.id } });
      }
    },
    getVariant(value) {
      if (value === "ACTIVE") {
        return "text-success font-weight-bold";
      } else if (value === "REVOKED") {
        return "text-warning font-weight-bold";
      } else {
        return "text-danger font-weight-bold";
      }
    }
  },
  components: {
    CertificateDatatableHeader
  }
};
</script>
<style>
.truncate-text {
  max-width: 1px;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
}
.break-text-all {
  word-break: break-all;
}
</style>
